/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { Building2, FolderOpen, HelpCircle, History, Layers, LayoutDashboard, ListTodo, LogOut, Palette, Settings, Shield, Users, Webhook, X } from 'lucide-react';
import React from 'react';
import PropTypes from 'prop-types';
import { NavLink, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import AccountSwitcher from './AccountSwitcher';

const DAISY_THEMES = [
  "light", "dark", "cupcake", "bumblebee", "emerald", "corporate", "synthwave",
  "retro", "cyberpunk", "valentine", "halloween", "garden", "forest", "aqua",
  "lofi", "pastel", "fantasy", "wireframe", "black", "luxury", "dracula",
  "cmyk", "autumn", "business", "acid", "lemonade", "night", "coffee", "winter",
  "dim", "nord", "sunset", "caramellatte"
];

const ThemePreview = ({ theme }) => {
  return (
    <div data-theme={theme} className="bg-base-100 grid shrink-0 grid-cols-2 gap-0.5 rounded-md p-1 shadow-sm">
      <div className="bg-base-content size-1 rounded-full"></div>
      <div className="bg-primary size-1 rounded-full"></div>
      <div className="bg-secondary size-1 rounded-full"></div>
      <div className="bg-accent size-1 rounded-full"></div>
    </div>
  );
};

ThemePreview.propTypes = {
  theme: PropTypes.string,
};

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/workflows', icon: FolderOpen, label: 'Capture Workflows', permission: 'templates.view' },
  { to: '/assignments', icon: ListTodo, label: 'Assignments', end: true, permission: 'assignments.view' },
  { to: '/import-history', icon: History, label: 'Import History', permission: 'assignments.view' },
  { to: '/users', icon: Users, label: 'Users', permission: 'users.view' },
  { to: '/roles', icon: Shield, label: 'Roles', permission: 'roles.view' },
  { to: '/accounts', icon: Building2, label: 'Accounts', permission: 'accounts.view' },
  { to: '/sub-accounts', icon: Layers, label: 'Sub Accounts', permission: 'sub_accounts.view' },
  { to: '/webhooks', icon: Webhook, label: 'Webhooks', permission: 'webhooks.view' },
  { to: '/settings', icon: Settings, label: 'Settings', permission: 'settings.manage' },
  { to: '/help', icon: HelpCircle, label: 'Help & Docs' },

];

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const { user, accountName, logout, currentLogoUrl, currentTheme, setCurrentTheme } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();

  const isSubAccountContext = !!user?.subAccountUID;
  const logoSrc = currentLogoUrl || logo;
  const logoAlt = (isSubAccountContext && currentLogoUrl) ? 'Sub-Account Logo' : 'Send2Me Logo';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[250] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar Content */}
      <div className={`
        fixed lg:relative left-0 h-full inset-0 bg-base-200 z-[300] 
        transition-all duration-300 ease-in-out lg:translate-x-0 
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isCollapsed ? 'lg:w-20' : 'w-72'}
        flex flex-col lg:rounded-xl overflow-hidden border-r border-base-content/5
      `}>
        <div className='absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary/20 blur-[120px] rounded-full pointer-events-none'></div>
        <div className='absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-primary/10 blur-[120px] rounded-full pointer-events-none'></div>

        <div className={`flex items-center justify-between px-6 py-8 pt-10 lg:pt-8 ${isCollapsed ? 'lg:px-0 lg:flex-col lg:gap-6' : 'px-8'}`}>
          <div className={`flex items-center gap-3 ${isCollapsed ? 'lg:justify-center' : ''}`}>
            <div className={`flex-shrink-0 bg-base-100 shadow-lg p-1 rounded-xl transition-all duration-300 border border-base-content/10 ${isCollapsed ? 'w-10 h-10' : 'w-10 h-10'}`}>
              <img src={logoSrc} alt={logoAlt} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.src = logo; e.currentTarget.onerror = null; }} />
            </div>
            {!isCollapsed && (
              <span className="text-base-content font-black text-xl tracking-tight whitespace-nowrap uppercase custom-line-break">{user?.subAccountName || 'Send2Me'}</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Collapse Toggle Button (Desktop) */}
            <button
              onClick={onToggleCollapse}
              className="hidden lg:flex p-2 text-base-content/40 hover:text-primary hover:bg-base-content/10 rounded-xl transition-colors"
              title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              <X size={20} className={`transition-transform duration-300 ${isCollapsed ? 'rotate-45' : ''}`} />
            </button>

            {/* Close button for mobile */}
            <button
              onClick={onClose}
              className="lg:hidden p-2 text-base-content/40 hover:text-primary hover:bg-base-content/10 rounded-xl transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <nav className={`flex-1 overflow-y-auto no-scrollbar py-2 space-y-2 ${isCollapsed ? 'px-2' : 'px-4'}`}>
          {navItems
            .filter(item => {
              if (!item.permission) { return true; }
              const permissionParts = item.permission.split('.');
              if (permissionParts.length === 2) {
                const [resource, action] = permissionParts;
                return can[resource] && can[resource][action] && can[resource][action]();
              }
              return true;
            })
            .map(({ to, icon: Icon, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => {
                  if (window.innerWidth < 1024) { onClose(); }
                }}
                title={isCollapsed ? label : ""}
                className={({ isActive }) =>
                  `flex items-center gap-3 py-3 rounded-xl text-sm font-medium transition-all duration-300 group ${isCollapsed ? 'justify-center px-0' : 'px-4'
                  } ${isActive
                    ? 'bg-primary text-primary-content shadow-lg'
                    : 'text-base-content/60 hover:bg-base-content/10 hover:text-primary'
                  }`
                }
              >
                <Icon size={22} className="transition-transform duration-300 group-hover:scale-110 flex-shrink-0" />
                {!isCollapsed && <span className="whitespace-nowrap">{label}</span>}
              </NavLink>
            ))}
        </nav>

        {/* Theme Picker — hidden when in sub-account context */}
        {!isSubAccountContext && (
        <div className={`px-4 py-0 transition-all duration-300 ${isCollapsed ? 'px-2' : ''}`}>
          <div className="dropdown dropdown-top dropdown-end w-full">
            <label
              tabIndex={0}
              className={`flex items-center gap-3 p-2 bg-base-100/50 border border-base-content/10 rounded-2xl cursor-pointer hover:bg-base-content/10 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
            >
              <div className="w-8 h-8 flex items-center justify-center text-primary bg-primary/10 rounded-xl shrink-0">
                <Palette size={18} />
              </div>
              {!isCollapsed && (
                <div className="flex-1 flex items-center justify-between min-w-0">
                  <span className="text-base-content text-xs font-black uppercase tracking-widest truncate capitalize" title={currentTheme}>
                    {currentTheme}
                  </span>
                  <ThemePreview theme={currentTheme} />
                </div>
              )}
            </label>
            <ul
              tabIndex={0}
              className="custom-scrollbar dropdown-content bg-base-200 text-base-content rounded-xl left-0 h-[30.5rem] max-h-[calc(100vh-15rem)] overflow-y-auto border border-base-content/10 shadow-2xl mt-16"
            >
              <li className="menu-title sticky top-0 z-[2] bg-base-200/95 backdrop-blur-md py-3 px-4 mb-2 border-b border-base-content/10 w-full">
                <span className="text-[10px] uppercase tracking-[0.2em] font-black text-base-content/40">Select Theme</span>
              </li>
              <div className="flex flex-col gap-0.5 mb-2 px-2">
                {DAISY_THEMES.map(theme => (
                  <li key={theme} className="w-full">
                    <button
                      onClick={() => setCurrentTheme(theme)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-base-content/5 transition-all group ${currentTheme === theme ? 'bg-primary/10 text-primary' : 'text-base-content/70 hover:text-base-content'}`}
                    >
                      <ThemePreview theme={theme} />
                      <div className="flex-1 text-left truncate capitalize font-black text-sm tracking-tight" title={theme}>{theme}</div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className={`${currentTheme === theme ? 'text-primary' : 'invisible'} h-3 w-3 shrink-0`}
                      >
                        <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"></path>
                      </svg>
                    </button>
                  </li>
                ))}
              </div>
            </ul>
          </div>
        </div>
        )}

        <div className={`p-4 mt-auto transition-all duration-300 ${isCollapsed ? 'px-2' : 'px-4'} space-y-2`}>
          <AccountSwitcher isCollapsed={isCollapsed} />
          <div className={`glass-panel !bg-base-100/30 border-base-content/5 rounded-2xl p-4 transition-all duration-300 ${isCollapsed ? 'px-2' : ''}`}>
            <NavLink
              to="/profile"
              className={`flex items-center gap-3 ${isCollapsed ? 'lg:flex-col lg:items-center' : 'mb-4'} hover:opacity-80 transition-opacity`}
            >
              <div className="w-10 h-10 rounded-full bg-primary text-primary-content flex items-center justify-center font-black flex-shrink-0 border border-base-content/10 shadow-lg">
                {user?.displayName?.charAt(0).toUpperCase()}
              </div>
              {!isCollapsed && (
                <div className="min-w-0">
                  <p className="text-base-content text-sm font-black truncate" title={user?.displayName}>{user?.displayName}</p>
                  <p className="text-base-content/40 text-[10px] truncate uppercase tracking-widest font-black" title={accountName ?? user?.role.replace('_', ' ') ?? 'Platform'}>
                    {accountName ?? user?.role.replace('_', ' ') ?? 'Platform'}
                  </p>
                </div>
              )}
            </NavLink>


            <button
              onClick={handleLogout}
              title={isCollapsed ? "Logout" : ""}
              className={`flex items-center justify-center gap-2 text-base-content/40 hover:text-rose-500 text-[10px] font-black uppercase tracking-[0.2em] transition-colors w-full rounded-xl hover:bg-rose-500/10 ${isCollapsed ? 'py-3 mt-4' : 'py-2.5'}`}
            >
              <LogOut size={16} className="flex-shrink-0" />
              {!isCollapsed && <span>Logout</span>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;

Sidebar.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};
